/**
 * GotoForm renderer — opens the target route as a POP-OUT window and watches
 * for it to close. On close: auto-ticks the "reviewed" checkbox and (if the
 * action targets a property or client) refreshes captured_data with the
 * latest record so the workflow context banner stays fresh.
 *
 * Optional params:
 *   route              — the destination URL (with {token} substitution)
 *   title              — heading text above the button
 *   instructions       — body copy explaining the step
 *   section            — small caption after the route
 *   trigger_when       — auto-skip if the expression doesn't match capturedData
 *   refresh_target     — after popup closes, refetch this entity and merge the
 *                        fresh record into captured_data. Values: "property" | "client"
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ExternalLink, CheckCircle2 } from 'lucide-react';
import { InlineParamEdit } from '../intake/scriptEditControls';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function evaluateCondition(cond, data) {
  if (!cond) return true;
  const m = cond.match(/^\s*([a-z0-9_.]+)\s*==\s*['"]?([^'"]+)['"]?\s*$/i);
  if (!m) return true;
  const [, key, expected] = m;
  const parts = key.split('.');
  let v = data || {};
  for (const p of parts) v = v?.[p];
  return String(v) === String(expected);
}

export default function GotoForm({ action, capturedData, value, onChange, onNext, onBack, canBack,
                                    statusKey, taskId, onTemplateChanged }) {
  const params = action.params || {};
  const substitute = (t) => (t || '').replace(/\{([^}]+)\}/g, (_, k) => {
    const parts = k.split('.'); let v = capturedData || {};
    for (const p of parts) v = v?.[p];
    return v != null ? String(v) : '';
  });
  const route = substitute(params.route || '');
  const section = params.section || '';
  const title = params.title || 'Complete the form';
  const instructions = params.instructions || 'Open the target form, fill it in, then close the window to return here.';
  const refreshTarget = params.refresh_target || (route.startsWith('/properties/') ? 'property' : route.startsWith('/clients/') ? 'client' : route.startsWith('/appraisals/') ? 'appraisal' : null);

  // Iter303ag-fix7 — Inline-edit context for the pencil controls on
  // title + instructions. Requires the runner to pass statusKey /
  // taskId / onTemplateChanged (identical wiring to ScriptedFieldPrompt
  // and PropertyIntakeForm).
  const editCtx = (statusKey && taskId && action?.id) ? {
    statusKey, taskId, actionId: action.id, onTemplateChanged,
  } : null;

  const [done, setDone] = useState(!!value);
  const [popupOpen, setPopupOpen] = useState(false);
  const [refreshed, setRefreshed] = useState(false);
  const popupRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { setDone(!!value); setRefreshed(false); }, [action.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // iter303ak-fix6 · auto_patch on MOUNT (not on click).
  //
  // Why: modern browsers require popups to fire synchronously from a user
  // gesture. Previously `openForm()` did an `await api.patch(...)` BEFORE
  // `window.open(...)` — the await broke the user-activation window, the
  // browser blocked the popup silently, the poller then saw `pop.closed`
  // immediately and auto-ticked the "reviewed" checkbox → runner advanced
  // without the operator ever seeing the form. Running the PATCH here on
  // mount (which happens when the previous action's Next click lands the
  // operator on this action) means openForm() becomes purely synchronous
  // and the popup opens straight from the click.
  //
  // Example use: Quote Sent Task 1 Action 3 sets appraisal.appraisal_mode
  // from appraisal.report_variant so the Roof Appraisal tab opens on the
  // correct mode (one_comment vs multi_defect) with no picker.
  //   auto_patch:      "appraisal.appraisal_mode"
  //   auto_patch_from: "appraisal.report_variant"
  //   auto_patch_map:  "single_defect=one_comment|multi_defect=multi_defect"
  useEffect(() => {
    if (!params.auto_patch || !params.auto_patch_from || !capturedData?.appraisal_id) return;
    let cancelled = false;
    (async () => {
      try {
        const target = String(params.auto_patch).replace(/^appraisal\./, '');
        const src = String(params.auto_patch_from).replace(/^appraisal\./, '');
        const raw = (params.auto_patch_map || '').split('|')
          .map(p => p.split('=').map(s => s.trim()))
          .reduce((acc, [k, v]) => (k ? { ...acc, [k]: v } : acc), {});
        const rec = capturedData?.appraisal || capturedData?.appraisal_record || {};
        const srcVal = rec[src];
        const nextVal = raw[srcVal] !== undefined ? raw[srcVal] : srcVal;
        if (!cancelled && nextVal) {
          await api.patch(`/appraisals/${capturedData.appraisal_id}`, { [target]: nextVal });
        }
      } catch { /* non-fatal — user can pick manually on the target page */ }
    })();
    return () => { cancelled = true; };
  }, [action.id, params.auto_patch, params.auto_patch_from, params.auto_patch_map, capturedData?.appraisal_id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // If the template flags this goto_form with `auto_open_on_enter: true`,
  // fire the pop-out immediately on mount. This runs shortly after the
  // preceding action's Next click, so browsers' user-activation window
  // should let the popup through. If blocked, the button below is still
  // available for a manual open.
  useEffect(() => {
    if (params.auto_open_on_enter && !popupOpen && !popupRef.current) {
      // Small delay to let React settle the mount + allow the click's user
      // activation to still be "fresh" from the browser's perspective.
      const t = setTimeout(() => openForm(), 60);
      return () => clearTimeout(t);
    }
  }, [action.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup poll on unmount
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
  }, []);

  // Called once the popup is confirmed closed. Refreshes the target entity
  // record (so the context banner + downstream forms see fresh data) and
  // auto-ticks the reviewed checkbox.
  const onPopupClosed = async () => {
    setPopupOpen(false);
    if (refreshTarget && !refreshed) {
      try {
        if (refreshTarget === 'property' && capturedData?.property_id) {
          const res = await api.get(`/properties/${capturedData.property_id}`);
          onChange({ __refresh: { property_record: res.data }, confirmed: true });
        } else if (refreshTarget === 'client' && capturedData?.client_id) {
          const res = await api.get(`/clients/${capturedData.client_id}`);
          onChange({ __refresh: { client_record: res.data }, confirmed: true });
        } else if (refreshTarget === 'appraisal' && capturedData?.appraisal_id) {
          // iter303ak-fix2 · appraisal refresh so the Runner banner + downstream
          // steps see fresh appraisal_mode / condition data after the operator
          // uses the Roof Appraisal tab pop-out.
          const res = await api.get(`/appraisals/${capturedData.appraisal_id}`);
          onChange({ __refresh: { appraisal: res.data, appraisal_record: res.data }, confirmed: true });
        } else {
          onChange('confirmed');
        }
        setRefreshed(true);
      } catch (e) {
        onChange('confirmed');
      }
    } else {
      onChange('confirmed');
    }
    setDone(true);
  };

  const openForm = () => {
    if (!route) return;
    // If a popup for this action is already open, focus it instead of reopening
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }
    // iter303ak-fix6 · Purely synchronous — window.open MUST fire directly
    // from the user click to preserve user activation. auto_patch runs
    // asynchronously in the mount effect above, not here.
    const pop = window.open(route,
      `wf-goto-${action.id}`,
      'width=1400,height=900,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no,popup=1');
    if (!pop) {
      // Popup blocked → open as a normal tab. User will need to tick manually.
      window.open(route, '_blank', 'noopener');
      return;
    }
    popupRef.current = pop;
    setPopupOpen(true);
    pop.focus();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (!popupRef.current || popupRef.current.closed) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        onPopupClosed();
      }
    }, 400);
  };

  const toggleDone = () => {
    const next = !done;
    setDone(next);
    onChange(next ? 'confirmed' : '');
  };

  return (
    <div>
      <div className="bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-400 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-amber-600 font-semibold mb-2 flex items-center gap-1">
          📝 <InlineParamEdit
            editCtx={editCtx}
            paramKey="title"
            value={params.title || ''}
            placeholder="(no title set)"
            multiline={false}
            className="text-[10px] uppercase tracking-widest font-semibold"
            dataTestidPrefix="wf-goto-title-edit"
          />
          {!editCtx && (title || 'Complete the form')}
        </div>
        <div className="text-base text-slate-800 leading-relaxed mb-3" data-testid="wf-goto-instructions">
          <InlineParamEdit
            editCtx={editCtx}
            paramKey="instructions"
            value={params.instructions || ''}
            placeholder="(no instructions set)"
            multiline={true}
            className="text-base text-slate-800 leading-relaxed"
            dataTestidPrefix="wf-goto-instructions-edit"
          />
          {!editCtx && (instructions || 'Open the target form, fill it in, then close the window to return here.')}
        </div>
        <div className="text-sm text-slate-500 font-mono bg-slate-100 rounded px-3 py-1.5 inline-block">
          {route || '(no route set)'}{section ? ` · ${section}` : ''}
        </div>
      </div>

      <button onClick={openForm} disabled={!route}
        data-testid="wf-goto-open"
        className={`w-full px-6 py-4 mb-4 border-2 rounded font-semibold transition flex items-center justify-center gap-2
          ${popupOpen
            ? 'border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
            : 'border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100'} disabled:opacity-40`}>
        <ExternalLink size={18} />
        {popupOpen ? 'Form window open — bring to front' : 'Open form in a pop-out window'}
      </button>

      {popupOpen && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mb-4 flex items-center gap-2" data-testid="wf-goto-watching">
          <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          Watching the pop-out — close it when you&apos;re done and I&apos;ll auto-confirm this step.
        </div>
      )}

      {refreshed && (
        <div className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded p-2 mb-4" data-testid="wf-goto-refreshed">
          ✓ Latest record refreshed from the server.
        </div>
      )}

      <label className="flex items-center gap-3 p-4 mb-6 border-2 border-slate-200 rounded cursor-pointer hover:bg-slate-50">
        <input type="checkbox" checked={done} onChange={toggleDone}
          data-testid="wf-goto-done"
          className="w-5 h-5" />
        <CheckCircle2 size={20} className={done ? 'text-emerald-500' : 'text-slate-300'} />
        <span className={`text-sm font-medium ${done ? 'text-slate-700' : 'text-slate-500'}`}>
          I&apos;ve reviewed and saved the form
        </span>
      </label>

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={onNext} disabled={!done}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">Next →</button>
      </div>
    </div>
  );
}
